import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {
  CollisionDetection,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import type { Contact } from '@/types/email';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ChipDragPreview, ChipInput } from './ChipInput';
import type { ChipDragData } from './ChipInput';
import type { RecipientFieldKey } from '@/types/compose';

import { extractEmailAddress, joinAddressList, splitAddressList } from '@/lib/addressParsing';

/** Split an address string into chips (quote/angle-bracket aware — G10) */
const parseChips = splitAddressList;

/** Join chips back into a comma-separated string */
const joinChips = joinAddressList;

/** Droppable ids for the Cc/Bcc reveal buttons (drop opens the field). */
const SHOW_CC_DROP = 'show-cc';
const SHOW_BCC_DROP = 'show-bcc';

/**
 * The reveal buttons sit inside the To field's droppable, so plain
 * pointerWithin can resolve to the surrounding field instead of the small
 * button under the pointer. Give the buttons priority when hit.
 */
const chipCollisionDetection: CollisionDetection = (args) => {
  const collisions = pointerWithin(args);
  const reveal = collisions.find(
    (c) => c.id === SHOW_CC_DROP || c.id === SHOW_BCC_DROP,
  );
  if (reveal) return [reveal];
  return collisions.length > 0 ? collisions : rectIntersection(args);
};

function RevealDropButton({
  dropId,
  label,
  onClick,
}: {
  dropId: string;
  label: string;
  onClick: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: dropId });
  return (
    <Button
      ref={setNodeRef}
      type="button"
      variant="ghost"
      size="sm"
      tabIndex={-1}
      onClick={onClick}
      className={cn(
        'h-auto text-xs px-1.5 py-0.5',
        isOver && 'bg-primary/15 text-foreground ring-1 ring-primary/40',
      )}
    >
      {label}
    </Button>
  );
}

interface RecipientFieldsProps {
  to: string;
  cc: string;
  bcc: string;
  onToChange: (value: string) => void;
  onCcChange: (value: string) => void;
  onBccChange: (value: string) => void;
  showCc: boolean;
  showBcc: boolean;
  onShowCc: () => void;
  onShowBcc: () => void;
  onHideCc: () => void;
  onHideBcc: () => void;
  suggestedContacts: Contact[];
  focusedField: RecipientFieldKey | null;
  onFocusField: (field: RecipientFieldKey | null) => void;
  onActiveInputChange?: (value: string) => void;
  applyContactSuggestion: (field: RecipientFieldKey, contact: Contact) => void;
}

export function RecipientFields({
  to,
  cc,
  bcc,
  onToChange,
  onCcChange,
  onBccChange,
  showCc,
  showBcc,
  onShowCc,
  onShowBcc,
  onHideCc,
  onHideBcc,
  suggestedContacts,
  focusedField,
  onFocusField,
  onActiveInputChange,
  applyContactSuggestion,
}: RecipientFieldsProps) {
  const [toInput, setToInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [bccInput, setBccInput] = useState('');
  const [draggedChip, setDraggedChip] = useState<string | null>(null);

  // Distance/delay activation keeps chip clicks (remove ✕) and touch
  // scrolling working; only a deliberate drag picks the chip up.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current as ChipDragData | undefined;
    setDraggedChip(data?.chip ?? null);
  }, []);

  const handleDragCancel = useCallback(() => setDraggedChip(null), []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggedChip(null);
      const data = event.active.data.current as ChipDragData | undefined;
      const overId = event.over?.id;
      if (!data || overId == null) return;

      const target: RecipientFieldKey | null =
        overId === SHOW_CC_DROP
          ? 'cc'
          : overId === SHOW_BCC_DROP
            ? 'bcc'
            : overId === 'to' || overId === 'cc' || overId === 'bcc'
              ? overId
              : null;
      if (!target || target === data.field) return;

      const values: Record<RecipientFieldKey, string> = { to, cc, bcc };
      const setters: Record<RecipientFieldKey, (value: string) => void> = {
        to: onToChange,
        cc: onCcChange,
        bcc: onBccChange,
      };

      // Re-resolve the chip in the source list — a blur-commit during the
      // drag can append chips, so trust identity over the captured index.
      const sourceField = data.field as RecipientFieldKey;
      const sourceChips = parseChips(values[sourceField]);
      const sourceIndex =
        sourceChips[data.index] === data.chip
          ? data.index
          : sourceChips.indexOf(data.chip);
      if (sourceIndex === -1) return;

      setters[sourceField](joinChips(sourceChips.filter((_, i) => i !== sourceIndex)));

      const targetChips = parseChips(values[target]);
      const email = extractEmailAddress(data.chip).toLowerCase();
      if (!targetChips.some((c) => extractEmailAddress(c).toLowerCase() === email)) {
        setters[target](joinChips([...targetChips, data.chip]));
      }

      if (overId === SHOW_CC_DROP) onShowCc();
      if (overId === SHOW_BCC_DROP) onShowBcc();
    },
    [to, cc, bcc, onToChange, onCcChange, onBccChange, onShowCc, onShowBcc],
  );

  const handleToInputChange = useCallback(
    (value: string) => {
      setToInput(value);
      onActiveInputChange?.(value);
    },
    [onActiveInputChange],
  );

  const handleCcInputChange = useCallback(
    (value: string) => {
      setCcInput(value);
      onActiveInputChange?.(value);
    },
    [onActiveInputChange],
  );

  const handleBccInputChange = useCallback(
    (value: string) => {
      setBccInput(value);
      onActiveInputChange?.(value);
    },
    [onActiveInputChange],
  );

  const handleToChipsChange = useCallback(
    (chips: string[]) => onToChange(joinChips(chips)),
    [onToChange],
  );

  const handleCcChipsChange = useCallback(
    (chips: string[]) => onCcChange(joinChips(chips)),
    [onCcChange],
  );

  const handleBccChipsChange = useCallback(
    (chips: string[]) => onBccChange(joinChips(chips)),
    [onBccChange],
  );

  const handleSuggestionSelect = useCallback(
    (field: RecipientFieldKey, contact: Contact) => {
      applyContactSuggestion(field, contact);
      if (field === 'to') setToInput('');
      if (field === 'cc') setCcInput('');
      if (field === 'bcc') setBccInput('');
    },
    [applyContactSuggestion],
  );

  const ccBccButtons = (
    <>
      {!showCc && (
        <RevealDropButton dropId={SHOW_CC_DROP} label="Cc" onClick={onShowCc} />
      )}
      {!showBcc && (
        <RevealDropButton dropId={SHOW_BCC_DROP} label="Bcc" onClick={onShowBcc} />
      )}
    </>
  );

  const showCcBccButtons = !showCc || !showBcc;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={chipCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <ChipInput
        id="compose-to"
        dndId="to"
        chips={parseChips(to)}
        onChipsChange={handleToChipsChange}
        inputValue={toInput}
        onInputChange={handleToInputChange}
        suggestions={focusedField === 'to' ? suggestedContacts : []}
        onSuggestionSelect={(contact) => handleSuggestionSelect('to', contact)}
        placeholder="To"
        autoFocus
        onFocus={() => {
          onFocusField('to');
          onActiveInputChange?.(toInput);
        }}
        onBlur={() => { if (focusedField === 'to') onFocusField(null); }}
        rightContent={showCcBccButtons ? ccBccButtons : undefined}
      />

      {showCc && (
        <div className="relative flex items-center gap-1">
          <div className="flex-1">
            <ChipInput
              id="compose-cc"
              dndId="cc"
              chips={parseChips(cc)}
              onChipsChange={handleCcChipsChange}
              inputValue={ccInput}
              onInputChange={handleCcInputChange}
              suggestions={focusedField === 'cc' ? suggestedContacts : []}
              onSuggestionSelect={(contact) => handleSuggestionSelect('cc', contact)}
              placeholder="Cc"
              onFocus={() => {
                onFocusField('cc');
                onActiveInputChange?.(ccInput);
              }}
              onBlur={() => { if (focusedField === 'cc') onFocusField(null); }}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            tabIndex={-1}
            onClick={onHideCc}
            className="h-6 w-6 shrink-0"
            title="Remove Cc"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {showBcc && (
        <div className="relative flex items-center gap-1">
          <div className="flex-1">
            <ChipInput
              id="compose-bcc"
              dndId="bcc"
              chips={parseChips(bcc)}
              onChipsChange={handleBccChipsChange}
              inputValue={bccInput}
              onInputChange={handleBccInputChange}
              suggestions={focusedField === 'bcc' ? suggestedContacts : []}
              onSuggestionSelect={(contact) => handleSuggestionSelect('bcc', contact)}
              placeholder="Bcc"
              onFocus={() => {
                onFocusField('bcc');
                onActiveInputChange?.(bccInput);
              }}
              onBlur={() => { if (focusedField === 'bcc') onFocusField(null); }}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            tabIndex={-1}
            onClick={onHideBcc}
            className="h-6 w-6 shrink-0"
            title="Remove Bcc"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* Portaled to body: the compose dialog has a transformed ancestor,
          which would re-anchor the overlay's position:fixed and strand it
          off-screen. No drop animation: on a successful move the source chip
          unmounts, so the default "return to origin" animation reads as a
          failed drop. */}
      {createPortal(
        <DragOverlay dropAnimation={null}>
          {draggedChip ? <ChipDragPreview chip={draggedChip} /> : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}
